import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Pencil, Trash2, Upload } from "lucide-react";
import { api, errMsg, imgSrc } from "../lib/api";
import { useFetch } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { PageTitle, Panel, Empty, Field, inputCls, btnPrimary } from "./ui";
import { BrandLoader } from "../components/site/Logo";

const LICENSES = { unknown: "Nepoznato – ne koristiti", own: "Vlastita fotografija", agency: "Agencija (licencirano)", press: "Press materijal", cc: "Creative Commons", permission: "Pisana dozvola autora" };

const MetaEditor = ({ m, reload }) => {
  const [f, setF] = useState({ alt_text: m.alt_text || "", caption: m.caption || "", credit: m.credit || "", source: m.source || "", license: m.license || "unknown", rights_confirmed: !!m.rights_confirmed, usage_notes: m.usage_notes || "" });
  const { data: usage } = useFetch(`/admin/media/${m.id}/usage`);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    try { await api.put(`/admin/media/${m.id}`, f); toast.success("Metapodaci sačuvani"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2 border-t border-border p-3 text-xs" data-testid={`media-meta-${m.id}`}>
      <Field label="Alt tekst"><input value={f.alt_text} onChange={set("alt_text")} className={inputCls} data-testid={`media-meta-alt-${m.id}`} /></Field>
      <Field label="Opis (caption)"><input value={f.caption} onChange={set("caption")} className={inputCls} /></Field>
      <Field label="Autor"><input value={f.credit} onChange={set("credit")} className={inputCls} /></Field>
      <Field label="Izvor"><input value={f.source} onChange={set("source")} className={inputCls} /></Field>
      <Field label="Licenca"><select value={f.license} onChange={set("license")} className={inputCls} data-testid={`media-meta-license-${m.id}`}>{Object.entries(LICENSES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
      <label className="flex items-center gap-2"><input type="checkbox" checked={f.rights_confirmed} onChange={set("rights_confirmed")} data-testid={`media-meta-rights-${m.id}`} /> Prava za objavu potvrđena</label>
      <p className="text-muted-foreground">Koristi se u: {usage?.length ? usage.map((a) => <Link key={a.id} to={`/admin/articles/${a.id}`} className="mr-1 underline">{a.headline}</Link>) : "nigdje"}</p>
      <button onClick={save} className={btnPrimary} data-testid={`media-meta-save-${m.id}`}>Sačuvaj</button>
    </div>
  );
};

const Item = ({ m, canManage, reload }) => {
  const [open, setOpen] = useState(false);
  const remove = async () => {
    if (!window.confirm("Ukloniti sliku iz biblioteke?")) return;
    try { await api.delete(`/admin/media/${m.id}`); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <figure className="overflow-hidden rounded-lg border border-border bg-card" data-testid={`media-item-${m.id}`}>
      <img src={imgSrc(m.url)} alt={m.alt_text} loading="lazy" className="aspect-[4/3] w-full object-cover" />
      <figcaption className="space-y-1 p-3 text-xs">
        <p className="truncate font-medium">{m.original_filename}</p>
        <p className="text-muted-foreground">{m.width}×{m.height} · {Math.round(m.size / 1024)} KB</p>
        <p className={m.rights_confirmed ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"} data-testid={`media-rights-${m.id}`}>{m.rights_confirmed ? `Prava: ${LICENSES[m.license]}` : "Prava nisu potvrđena"}</p>
        <div className="flex gap-3 pt-1">
          <button onClick={() => { navigator.clipboard.writeText(m.url); toast.success("URL kopiran"); }} className="inline-flex items-center gap-1 hover:text-pulse" data-testid={`media-copy-${m.id}`}><Copy className="h-3 w-3" /> URL</button>
          <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 hover:text-pulse" data-testid={`media-edit-${m.id}`}><Pencil className="h-3 w-3" /> Podaci</button>
          {canManage && <button onClick={remove} className="inline-flex items-center gap-1 text-destructive" data-testid={`media-delete-${m.id}`}><Trash2 className="h-3 w-3" /> Ukloni</button>}
        </div>
      </figcaption>
      {open && <MetaEditor m={m} reload={reload} />}
    </figure>
  );
};

export default function Media() {
  const { can } = useAuth();
  const [tick, setTick] = useState(0);
  const [q, setQ] = useState("");
  const { data, loading } = useFetch("/admin/media", [tick]);
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ alt_text: "", credit: "" });
  const [busy, setBusy] = useState(false);
  const reload = () => setTick((t) => t + 1);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Maksimalna veličina je 8 MB.");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("alt_text", meta.alt_text);
    fd.append("credit", meta.credit);
    setBusy(true);
    try {
      await api.post("/admin/media", fd);
      toast.success("Slika uploadovana – dopunite licencu i potvrdite prava");
      setFile(null);
      setMeta({ alt_text: "", credit: "" });
      e.target.reset();
      reload();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const list = (data || []).filter((m) => !q || `${m.original_filename} ${m.alt_text} ${m.credit} ${m.caption || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-media-page">
      <PageTitle title="Medijska biblioteka" sub="JPG, PNG ili WEBP · do 8 MB · EXIF/GPS podaci se uklanjaju. Slike s interneta koristite samo uz potvrđena prava." actions={<input placeholder="Pretraga medija" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} w-56`} data-testid="media-search" />} />
      {can("media.upload") && (
        <Panel title="Upload" className="mb-6">
          <form onSubmit={upload} className="grid gap-3 md:grid-cols-4">
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files[0])} className="text-sm" data-testid="media-file-input" />
            <input placeholder="Alt tekst" value={meta.alt_text} onChange={(e) => setMeta({ ...meta, alt_text: e.target.value })} className={inputCls} data-testid="media-alt-input" />
            <input placeholder="Autor / izvor fotografije" value={meta.credit} onChange={(e) => setMeta({ ...meta, credit: e.target.value })} className={inputCls} data-testid="media-credit-input" />
            <button disabled={!file || busy} className={btnPrimary} data-testid="media-upload-button"><Upload className="h-4 w-4" /> {busy ? "Upload…" : "Uploaduj"}</button>
          </form>
        </Panel>
      )}
      {loading && !data ? <BrandLoader /> : list.length ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="media-grid">
          {list.map((m) => <Item key={m.id} m={m} canManage={can("media.manage")} reload={reload} />)}
        </div>
      ) : <Empty testId="media-empty">Biblioteka je prazna.</Empty>}
    </div>
  );
}
